import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import { MAP_STYLE, MAP_CONFIG } from "../custom/map_style"

// The patch editor (TASK-156.4, doc-18 §7; decision-72 §3, TASK-259.2): one
// draggable circle that IS one of the member's patches. Follows
// map_picker_controller's shape — token value, the shared map style,
// refresh() on reveal, remove() on disconnect — plus the circle: a computed
// GeoJSON polygon (the ~30 lines of math below; no draw plugin, no turf), a
// draggable radius handle on the east edge, and counts refreshed from the
// preview endpoint on every settle.
//
// Unlike the single-circle page this replaces, the SAVE itself is a plain
// form submission (the surrounding <form>'s own Save button) — adding a
// name field made "drag updates hidden fields, Save posts the form" the
// simpler shape than auto-saving the geometry over fetch while a separate
// field waits for its own explicit save. This controller only keeps the
// hidden latitude/longitude/radius_m inputs and the live preview in sync.
//
// Connects to data-controller="patch-map"
export default class extends Controller {
  static targets = [
    "map", "sites", "tags", "size", "uncoordinated", "uncoordinatedNotice",
    "latitudeField", "longitudeField", "radiusField"
  ]

  static values = {
    token:     String,
    latitude:  { type: Number, default: 51.505 },
    longitude: { type: Number, default: -0.09 },
    radius:    { type: Number, default: 500 },
    previewUrl: String,
    // A rough, honest per-tag cache weight for the ~size figure
    // (decision-20: estimates shown). Kept as a value so the server owns it.
    bytesPerTag: { type: Number, default: 87040 }
  }

  connect() {
    if (!this.tokenValue) {
      console.error("patch-map: Mapbox token is required")
      return
    }

    mapboxgl.accessToken = this.tokenValue

    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style:     MAP_STYLE,
      config:    MAP_CONFIG,
      center:    [this.longitudeValue, this.latitudeValue],
      zoom:      this.zoomFor(this.radiusValue),
      interactive: true
    })
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    this.centre = new mapboxgl.Marker({ draggable: true, color: "#1d4ed8" })
      .setLngLat([this.longitudeValue, this.latitudeValue])
      .addTo(this.map)

    this.handle = new mapboxgl.Marker({ draggable: true, color: "#f97316", scale: 0.7 })
      .setLngLat(this.eastEdge())
      .addTo(this.map)

    this.centre.on("drag", () => this.redraw({ recentre: true }))
    this.centre.on("dragend", () => this.settled())
    this.handle.on("drag", () => this.resizeFromHandle())
    this.handle.on("dragend", () => this.settled())

    this.map.on("load", () => {
      this.map.addSource("patch-circle", { type: "geojson", data: this.circleGeoJSON() })
      this.map.addLayer({
        id: "patch-circle-fill", type: "fill", source: "patch-circle",
        paint: { "fill-color": "#1d4ed8", "fill-opacity": 0.12 }
      })
      this.map.addLayer({
        id: "patch-circle-line", type: "line", source: "patch-circle",
        paint: { "line-color": "#1d4ed8", "line-width": 2 }
      })
      this.syncHiddenFields()
      this.refreshCounts()
    })
  }

  disconnect() {
    if (this.map) this.map.remove()
  }

  // Re-fit after the container becomes visible — mapbox-gl renders 0×0
  // inside hidden containers (a modal that was not yet open when this
  // controller connected).
  refresh() {
    if (!this.map) return
    requestAnimationFrame(() => this.map.resize())
  }

  // The autocomplete fills coordinate fields and fires change events; a
  // postcode jump is those fields changing under us.
  jumpTo(event) {
    const target = event.currentTarget
    const latitude = parseFloat(target.dataset.latitude || target.value)
    const longitude = parseFloat(target.dataset.longitude || "")
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      this.latitudeValue = latitude
      this.longitudeValue = longitude
      this.centre.setLngLat([longitude, latitude])
      this.map.flyTo({ center: [longitude, latitude], zoom: this.zoomFor(this.radiusValue) })
      this.redraw({ recentre: false })
      this.settled()
    }
  }

  // ------------------------------------------------------------- geometry --

  // A circle as a 64-gon in lng/lat space. Metres-per-degree shrinks with
  // latitude on the east-west axis; near-constant north-south.
  circleGeoJSON() {
    const points = 64
    const [lng, lat] = [this.longitudeValue, this.latitudeValue]
    const metresPerDegreeLat = 111320
    const metresPerDegreeLng = metresPerDegreeLat * Math.max(Math.cos((lat * Math.PI) / 180), 0.01)

    const ring = []
    for (let i = 0; i <= points; i++) {
      const angle = (i / points) * 2 * Math.PI
      ring.push([
        lng + (this.radiusValue * Math.cos(angle)) / metresPerDegreeLng,
        lat + (this.radiusValue * Math.sin(angle)) / metresPerDegreeLat
      ])
    }
    return { type: "Feature", geometry: { type: "Polygon", coordinates: [ring] } }
  }

  eastEdge() {
    const metresPerDegreeLng =
      111320 * Math.max(Math.cos((this.latitudeValue * Math.PI) / 180), 0.01)
    return [this.longitudeValue + this.radiusValue / metresPerDegreeLng, this.latitudeValue]
  }

  resizeFromHandle() {
    const handle = this.handle.getLngLat()
    const metresPerDegreeLng =
      111320 * Math.max(Math.cos((this.latitudeValue * Math.PI) / 180), 0.01)
    const radius = Math.abs(handle.lng - this.longitudeValue) * metresPerDegreeLng
    // The model's range, enforced at the handle so the save cannot fail.
    this.radiusValue = Math.round(Math.min(Math.max(radius, 100), 10000))
    // Dragging the handle must not also drag the circle's centre.
    this.handle.setLngLat([this.eastEdge()[0], this.latitudeValue])
    this.redraw({ recentre: false })
  }

  redraw({ recentre }) {
    if (recentre) {
      const centre = this.centre.getLngLat()
      this.latitudeValue = centre.lat
      this.longitudeValue = centre.lng
      this.handle.setLngLat(this.eastEdge())
    }
    const source = this.map.getSource("patch-circle")
    if (source) source.setData(this.circleGeoJSON())
    this.syncHiddenFields()
  }

  zoomFor(radius) {
    // Wide enough to see the whole circle with margin; log2 because zoom is.
    return Math.max(9, Math.min(15, 14.7 - Math.log2(radius / 500)))
  }

  // ------------------------------------------------------------ the wire --

  // Keeps the surrounding <form>'s own hidden inputs current, so its Save
  // button submits whatever the map last settled on.
  syncHiddenFields() {
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = this.latitudeValue
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = this.longitudeValue
    if (this.hasRadiusFieldTarget) this.radiusFieldTarget.value = this.radiusValue
  }

  // Release = intent for the PREVIEW, not the save — the form's Save button
  // is the actual commit.
  settled() {
    this.syncHiddenFields()
    this.refreshCounts()
  }

  async refreshCounts() {
    try {
      const fence = `${this.latitudeValue},${this.longitudeValue},${this.radiusValue}`
      const response = await fetch(`${this.previewUrlValue}?fence=${encodeURIComponent(fence)}`, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return
      const counts = await response.json()

      if (this.hasSitesTarget) this.sitesTarget.textContent = counts.sites
      if (this.hasTagsTarget) this.tagsTarget.textContent = counts.tags
      if (this.hasSizeTarget) {
        const megabytes = (counts.tags * this.bytesPerTagValue) / (1024 * 1024)
        this.sizeTarget.textContent = `~${Math.max(1, Math.round(megabytes))} MB`
      }
      if (this.hasUncoordinatedTarget) {
        this.uncoordinatedTarget.textContent = counts.uncoordinated_sites
      }
      if (this.hasUncoordinatedNoticeTarget) {
        this.uncoordinatedNoticeTarget.hidden = counts.uncoordinated_sites === 0
      }
    } catch (error) {
      console.error("patch-map: preview failed", error)
    }
  }
}
